import type { ReactElement } from 'react';
import type {
    BrowserIconKey,
    BrowserIconsProps,
    PlatformIconKey,
    PlatformIconsProps,
} from './types';

import styles from './GameParameters.module.css';

// Brand marks sourced from Simple Icons (CC0 1.0): https://simpleicons.org/

function DesktopIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.platformIcon}>
            <path
                fill="currentColor"
                d="M20 3H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h6v2H8v2h8v-2h-2v-2h6c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 13H4V5h16v11z"
            />
        </svg>
    );
}

function AndroidIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.platformIcon}>
            <path
                fill="currentColor"
                d="M18.4395 5.5586c-.675 1.1664-1.352 2.3318-2.0274 3.498-.0366-.0155-.0742-.0286-.1113-.043-1.8249-.6957-3.484-.8-4.42-.787-1.8551.0185-3.3544.4643-4.2597.8203-.084-.1494-1.7526-3.021-2.0215-3.4864a1.1451 1.1451 0 0 0-.1406-.1914c-.3312-.364-.9054-.4859-1.379-.203-.475.282-.7136.9361-.3886 1.5019 1.9466 3.3696-.0966-.2158 1.9473 3.3593.0172.031-.4946.2642-1.3926 1.0177C2.8987 12.176.452 14.772 0 18.9902h24c-.119-1.1108-.3686-2.099-.7461-3.0683-.7438-1.9118-1.8435-3.2928-2.7402-4.1836a12.1048 12.1048 0 0 0-2.1309-1.6875c.6594-1.122 1.312-2.2559 1.9649-3.3848.2077-.3615.1886-.7956-.0079-1.1191a1.1001 1.1001 0 0 0-.8515-.5332c-.5225-.0536-.9392.3128-1.0488.5449zm-.0391 8.461c.3944.5926.324 1.3306-.1563 1.6503-.4799.3197-1.188.0985-1.582-.4941-.3944-.5927-.324-1.3307.1563-1.6504.4727-.315 1.1812-.1086 1.582.4941zM7.207 13.5273c.4803.3197.5506 1.0577.1563 1.6504-.394.5926-1.1038.8138-1.584.4941-.48-.3197-.5503-1.0577-.1563-1.6504.4008-.6021 1.1087-.8106 1.584-.4941z"
            />
        </svg>
    );
}

function AppleIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.platformIcon}>
            <path
                fill="currentColor"
                d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
            />
        </svg>
    );
}

function ChromeIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.browserIcon}>
            <path
                fill="currentColor"
                d="M12 0C8.21 0 4.831 1.757 2.632 4.501l3.953 6.848A5.454 5.454 0 0 1 12 6.545h10.691A12 12 0 0 0 12 0zM1.931 5.47A11.943 11.943 0 0 0 0 12c0 6.012 4.42 10.991 10.189 11.864l3.953-6.847a5.45 5.45 0 0 1-6.865-2.29zm13.342 2.166a5.446 5.446 0 0 1 1.45 7.09l.002.001h-.002l-5.344 9.257c.206.01.413.016.621.016 6.627 0 12-5.373 12-12 0-1.54-.29-3.011-.818-4.364zM12 16.364a4.364 4.364 0 1 1 0-8.728 4.364 4.364 0 0 1 0 8.728Z"
            />
        </svg>
    );
}

function FirefoxIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.browserIcon}>
            <path
                fill="currentColor"
                d="M8.824 7.287c.008 0 .004 0 0 0zm-2.8-1.4c.006 0 .003 0 0 0zm16.754 2.161c-.505-1.215-1.53-2.528-2.333-2.943.654 1.283 1.033 2.57 1.177 3.53l.002.02c-1.314-3.278-3.544-4.6-5.366-7.477-.091-.147-.184-.292-.273-.446a3.545 3.545 0 01-.13-.24 2.118 2.118 0 01-.172-.46.03.03 0 00-.027-.03.038.038 0 00-.021 0l-.006.001a.037.037 0 00-.01.005L15.624 0c-2.585 1.515-3.657 4.168-3.932 5.856a6.197 6.197 0 00-2.305.587.297.297 0 00-.147.37c.057.162.24.24.396.17a5.622 5.622 0 012.008-.523l.067-.005a5.847 5.847 0 011.957.222l.095.03a5.816 5.816 0 01.616.228c.08.036.16.073.238.112l.107.055a5.835 5.835 0 01.368.211 5.953 5.953 0 012.034 2.104c-.62-.437-1.733-.868-2.803-.681 4.183 2.09 3.06 9.292-2.737 9.02a5.164 5.164 0 01-1.513-.292 4.42 4.42 0 01-.538-.232c-1.42-.735-2.593-2.121-2.74-3.806 0 0 .537-2 3.845-2 .357 0 1.38-.998 1.398-1.287-.005-.095-2.029-.9-2.817-1.677-.422-.416-.622-.616-.8-.767a3.47 3.47 0 00-.301-.227 5.388 5.388 0 01-.032-2.842c-1.195.544-2.124 1.403-2.8 2.163h-.006c-.46-.584-.428-2.51-.402-2.913-.006-.025-.343.176-.389.206-.406.29-.787.616-1.136.974-.397.403-.76.839-1.085 1.303a9.816 9.816 0 00-1.562 3.52c-.003.013-.11.487-.19 1.073-.013.09-.026.181-.037.272a7.8 7.8 0 00-.069.667l-.002.034-.023.387-.001.06C.386 18.795 5.593 24 12.016 24c5.752 0 10.527-4.176 11.463-9.661.02-.149.035-.298.052-.448.232-1.994-.025-4.09-.753-5.844z"
            />
        </svg>
    );
}

function EdgeIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.browserIcon}>
            <path
                fill="currentColor"
                d="M21.86 17.86q.14 0 .25.12.1.13.1.25t-.11.33l-.32.46-.43.53-.44.5q-.21.25-.38.42l-.22.23q-.58.53-1.34 1.04-.76.51-1.6.91-.86.4-1.74.64t-1.67.24q-.9 0-1.69-.28-.8-.28-1.48-.78-.68-.5-1.22-1.17-.53-.66-.92-1.44-.38-.77-.58-1.6-.2-.83-.2-1.67 0-1 .32-1.96.33-.97.87-1.8.14.95.55 1.77.41.82 1.02 1.5.6.68 1.38 1.21.78.54 1.64.9.86.36 1.77.56.92.2 1.8.2 1.12 0 2.18-.24 1.06-.23 2.06-.72l.2-.1.2-.05zm-15.5-1.27q0 1.1.27 2.15.27 1.06.78 2.03.51.96 1.24 1.77.74.82 1.66 1.4-1.47-.2-2.8-.74-1.33-.55-2.48-1.37-1.15-.83-2.08-1.9-.92-1.07-1.58-2.33T.36 14.94Q0 13.54 0 12.06q0-.81.32-1.49.31-.68.83-1.23.53-.55 1.2-.96.66-.4 1.35-.66.74-.27 1.5-.39.78-.12 1.55-.12.7 0 1.42.1.72.12 1.4.35.68.23 1.32.57.63.35 1.16.83-.35 0-.7.07-.33.07-.65.23v-.02q-.63.28-1.2.74-.57.46-1.05 1.04-.48.58-.87 1.26-.38.67-.65 1.39-.27.71-.42 1.44-.15.72-.15 1.38zM11.96.06q1.7 0 3.33.39 1.63.38 3.07 1.15 1.43.77 2.62 1.93 1.18 1.16 1.98 2.7.49.94.76 1.96.28 1 .28 2.08 0 .89-.23 1.7-.24.8-.69 1.48-.45.68-1.1 1.22-.64.53-1.45.88-.54.24-1.11.36-.58.13-1.16.13-.42 0-.97-.03-.54-.03-1.1-.12-.55-.1-1.05-.28-.5-.19-.84-.5-.12-.09-.23-.24-.1-.16-.1-.33 0-.15.16-.35.16-.2.35-.5.2-.28.36-.68.16-.4.16-.95 0-1.06-.4-1.96-.4-.91-1.06-1.64-.66-.74-1.52-1.28-.86-.55-1.79-.89-.84-.3-1.72-.44-.87-.14-1.76-.14-1.55 0-3.06.45T.94 7.55q.71-1.74 1.81-3.13 1.1-1.38 2.52-2.35Q6.68 1.1 8.37.58q1.7-.52 3.58-.52Z"
            />
        </svg>
    );
}

function SafariIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.browserIcon}>
            <path
                fill="currentColor"
                d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.182A9.818 9.818 0 0 1 21.818 12 9.818 9.818 0 0 1 12 21.818 9.818 9.818 0 0 1 2.182 12 9.818 9.818 0 0 1 12 2.182zm-.75 2.25v1.5h1.5v-1.5h-1.5zm-4.015 1.41 1.06 1.061 1.061-1.06-1.06-1.061-1.061 1.06zm9.53 0-1.06-1.06-1.061 1.06 1.06 1.062 1.061-1.061zM6.182 11.25v1.5h1.5v-1.5h-1.5zm10.136 0v1.5h1.5v-1.5h-1.5zM7.235 15.097l-1.061 1.06 1.06 1.062 1.062-1.061-1.061-1.061zm9.53 0-1.061 1.061 1.06 1.061 1.062-1.06-1.061-1.062zM11.25 16.068v1.5h1.5v-1.5h-1.5zm.012-8.25L7.5 16.5l8.682-3.762L11.262 7.818z"
            />
        </svg>
    );
}

function OperaIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.browserIcon}>
            <path
                fill="currentColor"
                d="M8.051 5.238c-1.328 1.566-2.186 3.883-2.246 6.48v.564c.061 2.598.918 4.912 2.246 6.479 1.721 2.236 4.279 3.654 7.139 3.654 1.756 0 3.4-.537 4.807-1.471C17.879 22.846 15.074 24 12 24c-.192 0-.383-.004-.57-.014C5.064 23.689 0 18.436 0 12 0 5.371 5.373 0 12 0h.045c3.055.012 5.84 1.166 7.953 3.055-1.408-.93-3.051-1.471-4.81-1.471-2.858 0-5.417 1.42-7.14 3.654h.003zM24 12c0 3.556-1.545 6.748-4.002 8.945-3.078 1.5-5.946.451-6.896-.205 3.023-.664 5.307-4.32 5.307-8.74 0-4.422-2.283-8.075-5.307-8.74.949-.654 3.818-1.703 6.896-.205C22.455 5.25 24 8.445 24 12z"
            />
        </svg>
    );
}

const PLATFORM_ICONS: Record<PlatformIconKey, () => ReactElement> = {
    desktop: DesktopIcon,
    android: AndroidIcon,
    apple: AppleIcon,
};

const BROWSER_ICONS: Record<BrowserIconKey, () => ReactElement> = {
    chrome: ChromeIcon,
    firefox: FirefoxIcon,
    edge: EdgeIcon,
    safari: SafariIcon,
    opera: OperaIcon,
};

const PLATFORM_LABELS: Record<PlatformIconKey, string> = {
    desktop: 'Desktop',
    android: 'Android',
    apple: 'Apple',
};

const BROWSER_LABELS: Record<BrowserIconKey, string> = {
    chrome: 'Chrome',
    firefox: 'Firefox',
    edge: 'Edge',
    safari: 'Safari',
    opera: 'Opera',
};

export function PlatformIcons({ values }: PlatformIconsProps) {
    if (!values.length) {
        return null;
    }

    return (
        <div className={styles.platformIcons}>
            {values.map((platform) => {
                const Icon = PLATFORM_ICONS[platform];
                const label = PLATFORM_LABELS[platform];

                return (
                    <span
                        key={platform}
                        className={styles.platformIconWrap}
                        title={label}
                        aria-label={label}
                    >
                        <Icon />
                    </span>
                );
            })}
        </div>
    );
}

export function BrowserIcons({ values }: BrowserIconsProps) {
    if (!values.length) {
        return null;
    }

    return (
        <div className={styles.browserIcons}>
            {values.map((browser) => {
                const Icon = BROWSER_ICONS[browser];
                const label = BROWSER_LABELS[browser];

                return (
                    <span
                        key={browser}
                        className={styles.browserIconWrap}
                        title={label}
                        aria-label={label}
                    >
                        <Icon />
                    </span>
                );
            })}
        </div>
    );
}
